import {
  ArrowBack as ArrowBackIcon,
  FileDownload as DownloadIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  InputAdornment,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { UserRole } from '../../types';
import { DateUtils } from '../../utils';
import { useGetActiveManufacturersQuery } from '../manufacturer/manufacturers-api-slice';
import { selectCurrentUser } from '../user/user-slice';
import { RiskToggleBar } from './components/risk-toggle-bar';
import {
  deriveProductLabel,
  exportPriceTotalsCsv,
  exportPriceTotalsPdf,
  fmtSize,
} from './export-utils';
import { useGetProductPriceSheetQuery } from './price-management-api-slice';
import { sizeTotalsProducts } from './price-sheet-utils';
import { ProductPriceItem } from './types';

const fmt = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });

/**
 * Product Size Price Totals Page
 * Read-only view showing internal cost breakdown by product category.
 * Uses the manufacturer price sheet endpoint to retrieve aggregated cost data.
 */
const ProductSizePriceTotalsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentUser = useAppSelector(selectCurrentUser);

  const manufacturerIdParam = searchParams.get('manufacturerId');
  const manufacturerId = manufacturerIdParam ? parseInt(manufacturerIdParam, 10) : undefined;
  const effectiveManufacturerId = manufacturerId ?? currentUser?.manufacturerId;

  const { data: manufacturers = [] } = useGetActiveManufacturersQuery(undefined, {
    skip: !effectiveManufacturerId,
  });

  const { data, isLoading, error } = useGetProductPriceSheetQuery(
    { manufacturerId: effectiveManufacturerId! },
    { skip: !effectiveManufacturerId }
  );

  const [selectedRiskLevel, setSelectedRiskLevel] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [search, setSearch] = useState('');

  // MSRP and costs do not depend on the dealer tier: one row per BOM record
  const totalsProducts = useMemo(() => sizeTotalsProducts(data?.products ?? []), [data]);

  // Collect distinct risk levels from the data; default to first on load
  const riskLevels = useMemo(() => {
    return Array.from(new Set(totalsProducts.map(p => p.riskLevel))).sort();
  }, [totalsProducts]);

  const effectiveRiskLevel = selectedRiskLevel ?? riskLevels[0] ?? null;

  // Products filtered by risk level
  const riskFiltered = useMemo(() => {
    if (!effectiveRiskLevel) return totalsProducts;
    return totalsProducts.filter(p => p.riskLevel === effectiveRiskLevel);
  }, [totalsProducts, effectiveRiskLevel]);

  // Distinct category names for tabs (in order of first appearance)
  const categories = useMemo(() => {
    const seen = new Set<string>();
    const result: string[] = [];
    riskFiltered.forEach(p => {
      if (!seen.has(p.category)) {
        seen.add(p.category);
        result.push(p.category);
      }
    });
    return result;
  }, [riskFiltered]);

  // Products filtered by category tab and search
  const filtered = useMemo(() => {
    let items = riskFiltered;
    if (activeCategory !== 'all') items = items.filter(p => p.category === activeCategory);
    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter(
        p =>
          p.category.toLowerCase().includes(q) ||
          deriveProductLabel(p).toLowerCase().includes(q) ||
          (p.code && p.code.toLowerCase().includes(q)) ||
          String(p.averageSize).includes(q)
      );
    }
    return items;
  }, [riskFiltered, activeCategory, search]);

  // Group filtered items by category for the "All" view
  const grouped = useMemo(() => {
    const map = new Map<string, ProductPriceItem[]>();
    filtered.forEach(p => {
      if (!map.has(p.category)) map.set(p.category, []);
      map.get(p.category)!.push(p);
    });
    return Array.from(map.entries());
  }, [filtered]);

  const manufacturerName =
    manufacturers.find(m => m.id === effectiveManufacturerId)?.licenseeName ??
    currentUser?.manufacturer ??
    data?.manufacturerName ??
    '';

  const markupPct = data?.products?.[0]
    ? `${Math.round(data.products[0].pricing.markupPercentage * 100)}%`
    : '—';

  if (currentUser?.role === UserRole.DEALER) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Product Size Price Totals
        </Typography>
        <Alert severity="error" sx={{ mt: 1 }}>
          <strong>Access Restricted</strong> — The Product Size Price Totals page is not available
          for Dealer accounts. Please contact your manufacturer representative for pricing
          information.
        </Alert>
      </Box>
    );
  }

  if (!effectiveManufacturerId) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Product Size Price Totals
        </Typography>
        <Alert severity="warning">
          No manufacturer selected. Please navigate here from the Material Pricing Calculator.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Page header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            size="small"
            variant="outlined"
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
          <Box sx={{ ml: 1 }}>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              Product Size Price Totals
            </Typography>
            {manufacturerName && (
              <Typography variant="body2" color="text.secondary">
                {manufacturerName}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Download as CSV">
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                disabled={!data || filtered.length === 0}
                onClick={() => exportPriceTotalsCsv(filtered, manufacturerName, data!.generatedAt)}
              >
                CSV
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Download as PDF">
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                disabled={!data || filtered.length === 0}
                onClick={() =>
                  exportPriceTotalsPdf(
                    filtered,
                    manufacturerName,
                    data!.generatedAt,
                    riskLevels.indexOf(effectiveRiskLevel!) === 0
                      ? 'Risk I'
                      : 'Risk II - Permanent Dwelling'
                  )
                }
              >
                PDF
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Info bars */}
      {data && (
        <Paper
          variant="outlined"
          sx={{
            px: 2,
            py: 1,
            mb: 2,
            bgcolor: 'success.50',
            borderColor: 'success.200',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="body2" color="success.dark">
            Showing data from Published BOM
            {data.bomVersion != null ? ` Version ${data.bomVersion}` : ''}
            {data.effectiveDate
              ? ` (Effective: ${DateUtils.parseDate(data.effectiveDate).toFormat('MMM d, yyyy')})`
              : ''}
          </Typography>
          {data.products.length > 0 && (
            <Typography variant="body2" color="warning.dark" sx={{ fontWeight: 500 }}>
              MSRP Markup: {markupPct} applied to material costs (steel + paint + hardware)
            </Typography>
          )}
          <Typography variant="body2" color="success.dark">
            Generated {DateUtils.parseDate(data.generatedAt).toFormat('MMM d, yyyy')}
          </Typography>
        </Paper>
      )}

      {/* Risk level toggle — always visible; Risk II button disabled when no data */}
      <RiskToggleBar
        riskLevels={riskLevels}
        effectiveRiskLevel={effectiveRiskLevel}
        onSelect={setSelectedRiskLevel}
      />

      {/* Loading / error states */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}
      {!!error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load price totals. Please try again.
        </Alert>
      )}

      {data && !isLoading && data.products.length > 0 && (
        <>
          {/* Category tabs + search */}
          <Paper
            variant="outlined"
            sx={{ mb: 0, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', px: 2, gap: 1 }}>
              <Tabs
                value={activeCategory}
                onChange={(_, val) => setActiveCategory(val)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  flex: 1,
                  minHeight: 48,
                  '& .MuiTab-root': {
                    minHeight: 48,
                    fontSize: '0.8125rem',
                    textTransform: 'none',
                    py: 0,
                  },
                }}
              >
                <Tab label="All" value="all" />
                {categories.map(cat => (
                  <Tab key={cat} label={cat} value={cat} />
                ))}
              </Tabs>
              <Divider orientation="vertical" flexItem sx={{ my: 1 }} />
              <TextField
                size="small"
                placeholder="Search by product..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                sx={{ width: 220, my: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Paper>

          {/* Data grid */}
          <Paper
            variant="outlined"
            sx={{ borderTopLeftRadius: 0, borderTopRightRadius: 0, overflow: 'hidden' }}
          >
            {grouped.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  No products match the current filter.
                </Typography>
              </Box>
            ) : (
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 900 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      {[
                        'Code',
                        'Product',
                        'Size',
                        'Cost of Steel',
                        'Cost of Paint',
                        'Hardware Cost',
                        'Labor Cost',
                        'Total Cost',
                        'MSRP',
                        'Revenue',
                        'Cost/ft',
                      ].map(label => (
                        <TableCell
                          key={label}
                          align={
                            label === 'Code' || label === 'Product' || label === 'Size'
                              ? 'left'
                              : 'right'
                          }
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.8125rem',
                            py: 1.25,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {grouped.map(([category, items]) => (
                      <React.Fragment key={category}>
                        {/* Category header row */}
                        <TableRow>
                          <TableCell
                            colSpan={11}
                            sx={{
                              bgcolor: 'primary.main',
                              color: 'white',
                              fontWeight: 600,
                              fontSize: '0.8125rem',
                              py: 0.75,
                              px: 1.5,
                            }}
                          >
                            {category}
                            <Chip
                              label={
                                riskLevels.indexOf(effectiveRiskLevel!) === 0
                                  ? 'Risk I'
                                  : 'Risk II - Permanent Dwelling'
                              }
                              size="small"
                              sx={{
                                ml: 1,
                                height: 18,
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                bgcolor:
                                  riskLevels.indexOf(effectiveRiskLevel!) === 0
                                    ? '#e3f2fd'
                                    : '#fff3e0',
                                color:
                                  riskLevels.indexOf(effectiveRiskLevel!) === 0
                                    ? '#1565c0'
                                    : '#e65100',
                              }}
                            />
                          </TableCell>
                        </TableRow>

                        {/* Product rows */}
                        {items.map((item, idx) => (
                          <TableRow
                            key={`${category}-${idx}`}
                            hover
                            sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                          >
                            <TableCell
                              sx={{
                                fontSize: '0.8125rem',
                                fontFamily: 'monospace',
                                color: 'text.secondary',
                              }}
                            >
                              {item.code ?? '—'}
                              {item.isPriced === false && (
                                <Tooltip title={(item.missingMaterials ?? []).join(', ')}>
                                  <Chip
                                    label="Missing Price"
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                    sx={{ ml: 1, height: 18, fontSize: '0.7rem' }}
                                  />
                                </Tooltip>
                              )}
                            </TableCell>
                            <TableCell
                              sx={{
                                fontSize: '0.8125rem',
                                fontFamily: 'monospace',
                                color: 'primary.main',
                              }}
                            >
                              {deriveProductLabel(item)}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.8125rem' }}>
                              {fmtSize(item.averageSize)}'
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ fontSize: '0.8125rem', fontFamily: 'monospace' }}
                            >
                              {fmt(item.costs.steelCost)}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ fontSize: '0.8125rem', fontFamily: 'monospace' }}
                            >
                              {fmt(item.costs.paintCost)}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ fontSize: '0.8125rem', fontFamily: 'monospace' }}
                            >
                              {fmt(item.costs.hardwareCost)}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ fontSize: '0.8125rem', fontFamily: 'monospace' }}
                            >
                              {fmt(item.costs.laborCost)}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontSize: '0.8125rem',
                                fontFamily: 'monospace',
                                fontWeight: 600,
                              }}
                            >
                              {fmt(item.costs.totalCost)}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontSize: '0.8125rem',
                                fontFamily: 'monospace',
                                fontWeight: 600,
                                color: 'primary.main',
                              }}
                            >
                              {fmt(item.pricing.msrp)}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontSize: '0.8125rem',
                                fontFamily: 'monospace',
                                color: 'success.dark',
                              }}
                            >
                              {fmt(item.pricing.revenue)}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ fontSize: '0.8125rem', fontFamily: 'monospace' }}
                            >
                              {fmt(item.pricing.costPerFoot)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Footer */}
            <Box
              sx={{
                px: 2,
                py: 1,
                bgcolor: 'grey.50',
                borderTop: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Showing {filtered.length} of {riskFiltered.length} products (
                {effectiveRiskLevel
                  ? riskLevels.indexOf(effectiveRiskLevel) === 0
                    ? 'Risk I'
                    : 'Risk II - Permanent Dwelling'
                  : 'All'}
                )
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Generated {DateUtils.parseDate(data.generatedAt).toFormat('MMM d, yyyy, h:mm a')}
              </Typography>
            </Box>
          </Paper>
        </>
      )}

      {data && !isLoading && data.products.length === 0 && (
        <Alert severity="info" sx={{ mt: 1 }}>
          No published pricing data available for this manufacturer.
        </Alert>
      )}
    </Box>
  );
};

export default ProductSizePriceTotalsPage;
